import React, { useEffect, useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DraggableAttributes,
} from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useIntl } from "react-intl";
import overrideClasses from "../../../utils/overrideClasses";
import images from "../../../images";

export type BodyElement = {
  element: React.ReactNode;
  colSpan?: number;
  additionalClassName?: string;
  onCellClick?: React.MouseEventHandler<HTMLTableCellElement>;
};

export type BodyRow = {
  id: string;
  rowElements: BodyElement[];
  onRowClick?: React.MouseEventHandler<HTMLTableRowElement>;
  rowClassName?: string;
};

type BodyProps = {
  bodyArray?: BodyRow[];
  spanNoData: number;
  noDataText: string;
  sortable?: boolean;
  saveElementOrder?: (id: string, ids: string[]) => void;
};

type DragHandleProps = {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
};

const DragHandle = ({ attributes, listeners }: DragHandleProps) => (
  <button
    type="button"
    aria-label="Déplacer la ligne"
    {...attributes}
    {...listeners}
    style={{ touchAction: "none" }}
    className="cursor-grab active:cursor-grabbing px-2"
  >
    <img src={images.drag} alt="⋮⋮" aria-hidden="true" />
  </button>
);

const TableRow = ({
  row,
  renderCell,
  sortable,
}: {
  row: BodyRow;
  renderCell: (
    bodyElement: BodyElement,
    index: number,
    array: BodyElement[],
    dragHandle?: React.ReactNode
  ) => JSX.Element;
  sortable?: boolean;
}): JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: row.id });

  const style = sortable
    ? { transform: CSS.Transform.toString(transform), transition }
    : undefined;

  return (
    <tr
      ref={sortable ? setNodeRef : undefined}
      style={style}
      onClick={row.onRowClick}
      className={overrideClasses(
        row.onRowClick ? "cursor-pointer" : "",
        row.rowClassName
      )}
    >
      {row.rowElements.map((cell, index, array) =>
        renderCell(
          cell,
          index,
          array,
          sortable && index === array.length - 1 ? (
            <DragHandle attributes={attributes} listeners={listeners} />
          ) : undefined
        )
      )}
    </tr>
  );
};

const Body = ({
  bodyArray = [],
  spanNoData,
  noDataText,
  saveElementOrder,
  sortable = false,
}: BodyProps): JSX.Element => {
  const [items, setItems] = useState<BodyRow[]>(bodyArray);
  const intl = useIntl();

  useEffect(() => {
    setItems(bodyArray);
  }, [bodyArray]);

  const cellClassName =
    "px-5 py-3 text-14px leading-5 text-neutral-900 border-b border-neutral-100";

  const renderCell = useCallback(
    (
      bodyElement: BodyElement,
      index: number,
      array: BodyElement[],
      dragHandle?: React.ReactNode
    ) => (
      <td
        key={`cell_${index}`}
        colSpan={bodyElement.colSpan}
        onClick={bodyElement.onCellClick}
        className={overrideClasses(
          cellClassName,
          bodyElement.onCellClick ? "cursor-pointer" : "",
          index === 0 ? "text-left border-l" : "text-center",
          index === array.length - 1 ? "border-r" : "",
          bodyElement.additionalClassName
        )}
      >
        {bodyElement.element}
        {dragHandle}
      </td>
    ),
    []
  );

  const renderNoData = (
    <tr>
      <td
        colSpan={spanNoData}
        className={overrideClasses(cellClassName, "text-center border")}
      >
        {noDataText
          ? intl.formatMessage({
              id: noDataText,
              defaultMessage: "Aucune donnée",
            })
          : noDataText || "Aucune donnée"}
      </td>
    </tr>
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setItems(prev => {
        const oldIndex = prev.findIndex(c => c.id === active.id);
        const newIndex = prev.findIndex(c => c.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;

        const newOrder = arrayMove(prev, oldIndex, newIndex);
        saveElementOrder?.(
          String(active.id),
          newOrder.map(c => c.id)
        );

        return newOrder;
      });
    },
    [saveElementOrder]
  );

  if (items.length === 0) {
    return <tbody>{renderNoData}</tbody>;
  }

  if (sortable && saveElementOrder) {
    return (
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map(r => r.id)}
          strategy={verticalListSortingStrategy}
        >
          <tbody>
            {items.map(row => (
              <TableRow
                key={row.id}
                row={row}
                renderCell={renderCell}
                sortable
              />
            ))}
          </tbody>
        </SortableContext>
      </DndContext>
    );
  }

  return (
    <tbody>
      {items.map(row => (
        <TableRow key={row.id} row={row} renderCell={renderCell} />
      ))}
    </tbody>
  );
};

export default Body;
